import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Grid, Drawer, Divider, Button, Typography } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DashboardWrapper from "../containers/DashboardWrapper";
import {
  setCurrentPage,
  setCurrentPageNumber,
  getHomeImageData,
  getHomeImageDataNtt,
  setHomeImageData,
  setHomeImageDataNtt,
  getHomeImageDataHokuden,
  getHomeMissionImageData,
  getSavedSelectedTagDataByEmail,
  getAllImageDataByFileName,
  setAllImageDataByFileName,
  setFilter,
  setMissionOnly,
  setNoReport,
  setSelectedMission,
  setSelectedMissionLabel,
  setSaitamaCsvSelection,
  requestSaitamaCsvExport,
  setSaitamaCsvExportError,
  clearSaitamaCsvExport,
} from "../actions";
import Period from "../components/Filter/Period";
import FilterApplied from "../components/Filter/FilterApplied";
import Calendar from "../components/Calendar/Calendar";
import PhotoTable from "../components/Table/PhotoTable";
import TagConfig from "../config/TagConfig";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import moment from "moment-timezone";
import LocalStorageHelper from "../utils/helper/localstorage_helper";
import {
  buildSaitamaCsvIdentity,
  toggleSaitamaCsvSelection,
  toggleSaitamaCsvSelections,
} from "../utils/helper/saitamaCsvSelection";
import {
  buildSaitamaCsvFilename,
  serializeSaitamaCsv,
} from "../utils/helper/saitamaCsvSerializer";
import { isSaitamaSeasonSelected } from "../config/SaitamaCityConfig";

const drawerWidth = 300;

export const isImageBulkDownloadAllowed = (downloadImage = []) =>
  downloadImage.length > 0 && downloadImage.length <= 10;

export const buildSaitamaCsvExportRequest = (
  downloadImage = [],
  saitamaCsvSelection = []
) => ({
  hasSelectedRows: downloadImage.length > 0,
  selectedCanonicalCount: saitamaCsvSelection.length,
});

const useStyles = makeStyles((theme) => ({
  contentGrid: {
    marginTop: theme.spacing(1),
    width: "100%",
  },
  contentGridSlim: {
    marginTop: theme.spacing(1),
    width: "90%",
  },
  root: {
    marginTop: 32,
    minWidth: 275,
  },
  title: {
    fontSize: 14,
  },
  date: {
    marginBottom: 12,
  },
  filtered: {
    marginTop: 10,
    marginLeft: 16,
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    top: 62,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-start",
  },
  tagTextWrapper: {
    padding: "16px",
  },
  tagTip: {
    fontWeight: "bold",
    marginBottom: 16,
  },
  tagText: {
    fontSize: "14px",
    marginBottom: 16,
  },
}));

const Home = ({
  setCurrentPage,
  setCurrentPageNumber,
  getHomeImageData,
  getHomeImageDataNtt,
  getHomeImageDataHokuden,
  getHomeMissionImageData,
  setFilter,
  currentPageNumber,
  periodType,
  missionOnly,
  missionData,
  selectedMission,
  getSavedSelectedTagDataByEmail,
  filterStore,
  seasonData,
  selectedSeason,
  minMaxDateRange,
  allImageDataByFilename,
  getAllImageDataByFileName,
  setAllImageDataByFileName,
  setMissionOnly,
  setNoReport,
  setSelectedMission,
  setSelectedMissionLabel,
  saitamaCsvSelection,
  setSaitamaCsvSelection,
  saitamaCsvExport,
  requestSaitamaCsvExport,
  setSaitamaCsvExportError,
  clearSaitamaCsvExport,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [tagData, setTagData] = useState([]);
  const [downloadImage, setDownloadImage] = useState([]);
  const lastFetchedPageRef = useRef(null);
  const lastSelectedSeasonRef = useRef(selectedSeason);
  const companyName = LocalStorageHelper.getCompany();

  useEffect(() => {
    setCurrentPage(window.location.pathname);
    setCurrentPageNumber(1);
    let copyFilter = { ...filterStore };
    copyFilter.tag = [];
    setFilter(copyFilter);
  }, []);

  useEffect(() => {
    if (companyName === "mitsubishi") {
      setMissionOnly(true);
      setNoReport(true);
    }
  }, [companyName]);

  const isSaitamaPhotography = isSaitamaSeasonSelected(
    localStorage.getItem("access_key"),
    selectedSeason
  );

  useEffect(() => {
    if (!isSaitamaPhotography || !saitamaCsvExport?.data?.length || saitamaCsvExport.loading || saitamaCsvExport.error) {
      return;
    }
    try {
      const csv = serializeSaitamaCsv(saitamaCsvExport.data);
      saveAs(
        new Blob([csv], { type: "text/csv;charset=utf-8;" }),
        buildSaitamaCsvFilename()
      );
      clearSaitamaCsvExport();
    } catch (error) {
      setSaitamaCsvExportError(error.message || "CSV出力に失敗しました");
    }
  }, [isSaitamaPhotography, saitamaCsvExport, setSaitamaCsvExportError, clearSaitamaCsvExport]);

  useEffect(() => {
    if (!missionOnly) {
      return;
    }
    if (missionData.length === 0) {
      return;
    }
    if (selectedMission !== "blank") {
      return;
    }
    const firstMission = missionData[0];
    if (!firstMission) {
      return;
    }
    setSelectedMission(firstMission.missionId.toString());
    setSelectedMissionLabel(firstMission.missionName || "");
  }, [
    missionOnly,
    missionData,
    selectedMission,
    setSelectedMission,
    setSelectedMissionLabel,
  ]);

  useEffect(() => {
    if (!missionOnly || companyName !== "mitsubishi") {
      return;
    }
    if (selectedMission === "blank") {
      return;
    }
    if (!minMaxDateRange[0] || !minMaxDateRange[1]) {
      return;
    }
    if (lastFetchedPageRef.current === currentPageNumber) {
      return;
    }
    if (filterStore.tag.length > 0) {
      getSavedSelectedTagDataByEmail();
      lastFetchedPageRef.current = currentPageNumber;
      return;
    }
    getHomeMissionImageData({
      pageNumber: currentPageNumber,
      sort: "default",
    });
    lastFetchedPageRef.current = currentPageNumber;
  }, [
    missionOnly,
    companyName,
    selectedMission,
    minMaxDateRange,
    currentPageNumber,
    filterStore.tag.length,
    getSavedSelectedTagDataByEmail,
    getHomeMissionImageData,
  ]);

  useEffect(() => {
    if (seasonData.length > 0) {
      const seasonChanged = lastSelectedSeasonRef.current !== selectedSeason;
      if (seasonChanged) {
        setHomeImageData([]);
        setHomeImageDataNtt([]);
        setAllImageDataByFileName([]);
        setSaitamaCsvSelection([]);
      }
      lastSelectedSeasonRef.current = selectedSeason;
      if (filterStore.tag.length > 0) {
        getSavedSelectedTagDataByEmail();
      } else {
        if (isSaitamaPhotography) {
          getHomeImageData({
            pageNumber: currentPageNumber,
            sort: "default",
          });
        } else if (companyName === "ntt" || companyName === "ntt-ft") {
          getHomeImageDataNtt({
            pageNumber: currentPageNumber,
            sort: "default",
          });
        } else if (companyName === "okiden") {
          getHomeImageDataNtt({
            pageNumber: currentPageNumber,
            sort: "default",
          });
        } else if (companyName === "hokuden") {
          getHomeImageDataHokuden({
            pageNumber: currentPageNumber,
            sort: "default",
          });
        } else if (companyName === "tepco" || companyName === "saitama-city") {
          getHomeImageData({
            pageNumber: currentPageNumber,
            sort: "default",
          });
        }
      }
    }
  }, [currentPageNumber, seasonData, selectedSeason, isSaitamaPhotography]);

  useEffect(() => {
    if (periodType === "season") {
      setTagData(TagConfig.seasonTag);
    } else {
      setTagData(TagConfig.missionTagB.concat(TagConfig.missionTagC));
    }
    setDownloadImage([]);
    setAllImageDataByFileName([]);
  }, [periodType]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleImageSelect = (event, value) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      // 選択時に配列へ追加
      setDownloadImage((prev) => [...prev, value]);
    } else {
      // 解除時に配列から削除
      setDownloadImage((prev) => prev.filter((url) => url !== value));
    }
  };

  useEffect(() => {
    if (allImageDataByFilename.length > 0) {
      zipDownload();
    }
  }, [allImageDataByFilename]);

  const zipDownload = async () => {
    const zip = new JSZip();
    const folder = zip.folder("images");

    if (!folder) return;

    // 現在日時を取得してフォーマット
    const currentDateTime = moment.tz("Asia/Tokyo").format("YYYYMMDD-HHmmss");

    // 各画像をフェッチして ZIP に追加
    await Promise.all(
      allImageDataByFilename.map(async (url, index) => {
        const response = await fetch(url);
        const blob = await response.blob();

        // URL からファイル名を抽出
        const fileNameWithExtension = url.split("/").pop(); // 最後の部分を取得
        const fileName = fileNameWithExtension.replace(/\.jpg$/, "");

        folder.file(`${fileName}.jpg`, blob);
      })
    );

    // ZIP を生成してダウンロード
    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(
      zipBlob,
      `ピクトレ画像-${allImageDataByFilename.length}件-${currentDateTime}.zip`
    );
  };

  const handleImageDownload = () => {
    getAllImageDataByFileName(downloadImage);
  };

  const handleSaitamaCsvSelect = (event, row) => {
    const identity = buildSaitamaCsvIdentity(row);
    setSaitamaCsvSelection(
      toggleSaitamaCsvSelection(
        saitamaCsvSelection,
        identity,
        event.target.checked
      )
    );
  };

  const handleSaitamaCsvSelectAll = (event, rows) => {
    const identities = rows.map(buildSaitamaCsvIdentity);
    setSaitamaCsvSelection(
      toggleSaitamaCsvSelections(
        saitamaCsvSelection,
        identities,
        event.target.checked
      )
    );
  };

  return (
    <DashboardWrapper>
      <Grid container spacing={0} direction="row" justifyContent="center">
        <Grid item xs={12} lg={12}>
          <Grid
            container
            justifyContent={"center"}
            className={open ? classes.contentGridSlim : classes.contentGrid}
          >
            <Grid item xs={12} lg={10}>
              <Grid
                container
                direction="row"
                justifyContent={"space-between"}
                className={open ? classes.contentGridSlim : classes.contentGrid}
              >
                <Grid item style={{marginLeft: 16}}>
                  <Period companyName={companyName} />
                </Grid>
                <Grid item>
                  <Calendar companyName={companyName} />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              lg={10}
              className={classes.filtered}
            >
              <Grid
                container
                direction="row"
                justifyContent={"space-between"}
                alignItems="flex-end"
                className={open ? classes.contentGridSlim : classes.contentGrid}
              >
                <Grid item style={{marginLeft: 16}}>
                  <FilterApplied companyName={companyName} />
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    disabled={!isImageBulkDownloadAllowed(downloadImage)}
                    onClick={handleImageDownload}
                    style={{ marginTop: 8 }}
                  >
                    画像一括ダウンロード
                  </Button>
                  {isSaitamaPhotography && (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={saitamaCsvExport?.loading}
                      onClick={() => requestSaitamaCsvExport(
                        buildSaitamaCsvExportRequest(downloadImage, saitamaCsvSelection)
                      )}
                      style={{ marginTop: 8, marginLeft: 8, color:"#fff" }}
                    >
                      {saitamaCsvExport?.loading ? "ダウンロード中..." : "点検結果をダウンロード"}
                    </Button>
                  )}
                  {isSaitamaPhotography && saitamaCsvExport?.error && (
                    <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
                      {saitamaCsvExport.error}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} lg={10} className={classes.filtered}>
              <PhotoTable
                handleDrawerOpen={handleDrawerOpen}
                handleDrawerClose={handleDrawerClose}
                handleImageSelect={handleImageSelect}
                handleSaitamaCsvSelect={handleSaitamaCsvSelect}
                handleSaitamaCsvSelectAll={handleSaitamaCsvSelectAll}
                saitamaCsvSelection={saitamaCsvSelection}
                downloadImage={downloadImage}
                setDownloadImage={setDownloadImage}
                open={open}
                activeDownload={true}
                companyName={companyName}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={open}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </div>
        <Divider />
        <div className={classes.tagTextWrapper}>
          {tagData.length > 0 && (
            <>
              <div className={classes.tagTip}>
                {periodType === "season" ? "シーズン" : "ミッション"}
                結果タグ説明
              </div>
              {tagData.length > 0 &&
                tagData.map((row, index) => (
                  <div className={classes.tagText} key={index}>
                    {row["tip"]}
                  </div>
                ))}
            </>
          )}
        </div>
      </Drawer>
    </DashboardWrapper>
  );
};

Home.propTypes = {
  classes: PropTypes.object,
};

const mapStateToProps = (state) => ({
  currentPageNumber: state.home.currentPageNumber,
  periodType: state.app.periodType,
  seasonData: state.app.seasonData,
  selectedSeason: state.app.selectedSeason,
  missionData: state.app.missionData,
  selectedMission: state.app.selectedMission,
  missionOnly: state.app.missionOnly,
  filterStore: state.home.filter,
  minMaxDateRange: state.app.minMaxDateRange,
  allImageDataByFilename: state.image.allImageDataByFilename,
  saitamaCsvSelection: state.home.saitamaCsvSelection,
  saitamaCsvExport: state.home.saitamaCsvExport,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  setCurrentPageNumber: (param) => dispatch(setCurrentPageNumber(param)),
  getHomeImageData: (param) => dispatch(getHomeImageData(param)),
  getHomeImageDataNtt: (param) => dispatch(getHomeImageDataNtt(param)),
  setHomeImageData: (param) => dispatch(setHomeImageData(param)),
  setHomeImageDataNtt: (param) => dispatch(setHomeImageDataNtt(param)),
  getHomeImageDataHokuden: (param) => dispatch(getHomeImageDataHokuden(param)),
  getSavedSelectedTagDataByEmail: () => dispatch(getSavedSelectedTagDataByEmail()),
  getAllImageDataByFileName: (param) => dispatch(getAllImageDataByFileName(param)),
  setAllImageDataByFileName: (param) => dispatch(setAllImageDataByFileName(param)),
  setFilter: (param) => dispatch(setFilter(param)),
  setMissionOnly: (param) => dispatch(setMissionOnly(param)),
  setNoReport: (param) => dispatch(setNoReport(param)),
  setSelectedMission: (param) => dispatch(setSelectedMission(param)),
  setSelectedMissionLabel: (param) => dispatch(setSelectedMissionLabel(param)),
  setSaitamaCsvSelection: (param) => dispatch(setSaitamaCsvSelection(param)),
  requestSaitamaCsvExport: (param) => dispatch(requestSaitamaCsvExport(param)),
  setSaitamaCsvExportError: (param) => dispatch(setSaitamaCsvExportError(param)),
  clearSaitamaCsvExport: () => dispatch(clearSaitamaCsvExport()),
  getHomeMissionImageData: (param) => dispatch(getHomeMissionImageData(param)),
});

export { mapDispatchToProps };

export default connect(mapStateToProps, mapDispatchToProps)(Home);
